/**
 * @fileoverview Job Settings → Conflict Check panel
 *
 * Purpose: hold the *record* of the firm-wide conflict poll run before the
 * engagement was accepted, on the job file where a reviewer would look for it.
 * The Pipeline Tracker panel is where a check is run, chased and closed; this
 * is the read-only outcome — who was polled, how they voted and why, who signed
 * it off — and it survives the job's conversion out of pre-appointment.
 *
 * Expected to sit under an Audit grouping in job settings once there are
 * enough audit surfaces to warrant one.
 *
 * Dependencies:
 * - Convex: preAppointment.conflicts.listForJob
 * - Components: ConflictPartySchedule (shared with the vote page and pipeline)
 */

import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { format } from "date-fns";
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { ConflictBriefView } from "@/components/pre-appointment/conflict-brief-view";
import { ConflictPartySchedule } from "@/components/pre-appointment/conflict-party-schedule";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";

type ConflictCheck = FunctionReturnType<
  typeof api.preAppointment.conflicts.listForJob
>[number];

/**
 * What the check actually concluded, in the terms a reviewer of the file cares
 * about. A flagged conflict outranks everything: one "yes" is the finding,
 * whether or not the rest of the firm has answered.
 */
type Outcome = "flagged" | "clear" | "partial" | "open";

interface OutcomeMeta {
  label: string;
  description: string;
  icon: LucideIcon;
  color: BadgeColor;
}

const OUTCOME_META: Record<Outcome, OutcomeMeta> = {
  flagged: {
    label: "Conflict declared",
    description:
      "At least one member of the firm declared a conflict. Their reason is recorded against their name below.",
    icon: ShieldAlert,
    color: "red",
  },
  clear: {
    label: "No conflicts declared",
    description:
      "Every member of the firm who was polled responded, and none declared a conflict.",
    icon: ShieldCheck,
    color: "green",
  },
  partial: {
    label: "No conflicts declared — some members did not respond",
    description:
      "No conflicts were declared, but the check was closed before everyone answered. Non-responders are listed below.",
    icon: ShieldQuestion,
    color: "amber",
  },
  open: {
    label: "Awaiting responses",
    description:
      "The poll is still open, so this is not yet a final result. Chase or close the check from the Pipeline Tracker.",
    icon: Clock,
    color: "blue",
  },
};

function outcomeOf(check: ConflictCheck): Outcome {
  if (check.tally.yes > 0) return "flagged";
  if (check.status === "open") return "open";
  return check.tally.pending > 0 ? "partial" : "clear";
}

export function JobConflictCheckPanel({ jobId }: { jobId: Id<"jobs"> }) {
  const { data: checks, isLoading } = useQuery(
    convexQuery(api.preAppointment.conflicts.listForJob, { jobId }),
  );
  const [viewDocId, setViewDocId] = useState<Id<"documents"> | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rows = checks ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold tracking-tight">Conflict Check</h2>
        <p className="text-sm text-muted-foreground">
          The record of the conflict poll circulated to the firm before this
          engagement was accepted — the parties checked, each member's vote and
          reason, and who closed the check. Checks are run and chased from the
          Pipeline Tracker.
        </p>
      </header>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No conflict check has been recorded for this job.
          </p>
          <Link
            to="/dashboard/pre-appointment"
            className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            <ExternalLink className="size-3" />
            Run one from the Pipeline Tracker
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Newest first, and the newest is the one a reviewer wants open. */}
          {rows.map((check, index) => (
            <ConflictCheckRecord
              key={String(check._id)}
              check={check}
              jobId={jobId}
              defaultOpen={index === 0}
              onViewDocument={setViewDocId}
            />
          ))}
        </div>
      )}

      <DocumentDetail
        documentId={viewDocId}
        isViewer={false}
        open={viewDocId !== null}
        onClose={() => setViewDocId(null)}
      />
    </div>
  );
}

function ConflictCheckRecord({
  check,
  jobId,
  defaultOpen,
  onViewDocument,
}: {
  check: ConflictCheck;
  /** The job whose record this is — a group check may anchor elsewhere. */
  jobId: Id<"jobs">;
  defaultOpen: boolean;
  onViewDocument: (documentId: Id<"documents">) => void;
}) {
  const [showParties, setShowParties] = useState(defaultOpen);
  const outcome = outcomeOf(check);
  const meta = OUTCOME_META[outcome];
  const OutcomeIcon = meta.icon;

  const responded = check.tally.yes + check.tally.no;
  const polled = check.responses.length;

  // Declared conflicts first — the reason for the record — then the rest of
  // the responses, then whoever never answered.
  const orderedResponses = useMemo(() => {
    const rank = (vote: string | undefined) =>
      vote === "yes" ? 0 : vote === "no" ? 1 : 2;
    return [...check.responses].sort(
      (a, b) =>
        rank(a.vote) - rank(b.vote) || a.voterName.localeCompare(b.voterName),
    );
  }, [check.responses]);

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      {/* Outcome */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="soft" color={meta.color} className="gap-1.5">
            <OutcomeIcon className="size-3.5" />
            {meta.label}
          </Badge>
          <Badge variant="outline" size="sm">
            {check.status === "open" ? "Open" : "Closed"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{meta.description}</p>
      </div>

      {/* Provenance */}
      <dl className="grid grid-cols-1 gap-3 border-t pt-4 text-sm sm:grid-cols-3">
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">Circulated</dt>
          <dd>
            {format(check._creationTime, "d MMM yyyy")} by {check.createdByName}
            {check.jobId !== jobId && check.entities[0] && (
              <> from {check.entities[0].name}</>
            )}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">Closed</dt>
          <dd>
            {check.closedAt
              ? `${format(check.closedAt, "d MMM yyyy")}${check.closedByName ? ` by ${check.closedByName}` : ""}`
              : "Still open"}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">Responses</dt>
          <dd>
            {responded} of {polled} member{polled === 1 ? "" : "s"} polled
            {check.tally.yes > 0 && (
              <span className="text-red-600">
                {" "}
                · {check.tally.yes} conflict
                {check.tally.yes === 1 ? "" : "s"}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {/* A group check (MOR-1419): every entity the one poll covered, so a
          reviewer of this file sees it was checked alongside the others. */}
      {check.entities.length > 1 && (
        <div className="flex flex-col gap-1 border-t pt-4 text-sm">
          <span className="text-xs text-muted-foreground">
            Entities covered by this check
          </span>
          <ul className="flex flex-wrap gap-1.5">
            {check.entities.map((entity, index) => (
              <li
                key={entity.jobId ? String(entity.jobId) : `name:${index}`}
                className="flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs"
              >
                <Building2 className="size-3 shrink-0 text-muted-foreground" />
                <span
                  className={entity.jobId === jobId ? "font-medium" : undefined}
                >
                  {entity.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Parties checked — the brief as it went out, plus its schedule. */}
      <div className="flex flex-col gap-2 border-t pt-4">
        <button
          type="button"
          className="flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground"
          onClick={() => setShowParties((v) => !v)}
        >
          {showParties ? (
            <ChevronDown className="size-3.5" />
          ) : (
            <ChevronRight className="size-3.5" />
          )}
          Parties checked
        </button>
        {showParties && (
          <>
            <ConflictBriefView description={check.description} />
            <ConflictPartySchedule partyLists={check.partyLists} />
          </>
        )}
      </div>

      {/* Documents that rode along with the check (MOR-939). */}
      {check.attachments.length > 0 && (
        <div className="flex flex-col gap-2 border-t pt-4">
          <h3 className="text-xs font-medium text-muted-foreground">
            Attached documents
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {check.attachments.map((attachment) => (
              <button
                key={String(attachment.documentId)}
                type="button"
                className="flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-muted"
                title="View document"
                onClick={() => onViewDocument(attachment.documentId)}
              >
                <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 truncate">{attachment.fileName}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Responses — the evidence, every member polled, non-responders kept. */}
      <div className="flex flex-col gap-2 border-t pt-4">
        <h3 className="text-xs font-medium text-muted-foreground">Responses</h3>
        <ul className="flex flex-col divide-y">
          {orderedResponses.map((response) => (
            <li
              key={String(response._id)}
              className="flex items-start gap-2 py-2 text-sm"
            >
              {response.vote === "yes" ? (
                <ShieldAlert className="mt-0.5 size-4 shrink-0 text-red-600" />
              ) : response.vote === "no" ? (
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600" />
              ) : (
                <Clock className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{response.voterName}</span>
                  <span className="text-xs text-muted-foreground">
                    {response.vote === "yes"
                      ? "declared a conflict"
                      : response.vote === "no"
                        ? "no conflict"
                        : response.emailStatus === "failed"
                          ? "no response — vote email failed to send"
                          : "no response"}
                    {response.respondedAt !== undefined &&
                      ` · ${format(response.respondedAt, "d MMM yyyy")}`}
                  </span>
                </div>
                {response.reason && (
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-muted-foreground">
                    {response.reason}
                  </p>
                )}
                <ResponseHistoryTrail response={response} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * A member's vote trail (MOR-965), shown only when their answer moved —
 * changed or withdrawn — so a reviewer sees how the response landed where
 * it did. A single straight vote needs no trail: the response line above
 * already records it.
 */
function ResponseHistoryTrail({
  response,
}: {
  response: ConflictCheck["responses"][number];
}) {
  const settled = response.vote !== undefined ? 1 : 0;
  if (response.voteHistory.length <= settled) return null;

  return (
    <ul className="mt-1 flex flex-col gap-0.5 border-l-2 border-muted pl-2">
      {[...response.voteHistory].reverse().map((entry, index) => (
        <li key={index} className="text-xs text-muted-foreground">
          {entry.action === "withdrawn"
            ? "Withdrew their vote"
            : entry.vote === "yes"
              ? "Declared a conflict"
              : "Voted no conflict"}
          {entry.action === "voted" && entry.reason
            ? ` — “${entry.reason}”`
            : ""}
          {" · "}
          {format(entry.at, "d MMM yyyy, h:mmaaa")}
        </li>
      ))}
    </ul>
  );
}
